import { useEffect, useState } from 'react';
import { usePortal } from '@/contexts/PortalContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, ExternalLink } from 'lucide-react';

const TYPE_COLORS = { contract: 'bg-[#4a9ac5]/10 text-[#2a6496]', proposal: 'bg-emerald-50 text-emerald-700', change_order: 'bg-amber-50 text-amber-700', invoice: 'bg-orange-50 text-orange-700', receipt: 'bg-violet-50 text-violet-700', warranty: 'bg-cyan-50 text-cyan-700', guide: 'bg-teal-50 text-teal-700', document: 'bg-gray-100 text-gray-600' };

export default function PortalDocuments() {
  const { api, user } = usePortal();
  const [docs, setDocs] = useState([]);
  const pid = user?.projects?.[0];

  useEffect(() => { if (pid) api(`/projects/${pid}/documents`).then(r => setDocs(r.data)).catch(() => {}); }, [api, pid]);

  const grouped = {};
  docs.forEach(d => { const t = d.type || 'document'; if (!grouped[t]) grouped[t] = []; grouped[t].push(d); });

  return (
    <div className="space-y-6" data-testid="portal-documents">
      <div>
        <h1 className="text-2xl font-extrabold text-[#1e3a50]">Documents</h1>
        <p className="text-sm font-medium text-[#6b8a9e]">{docs.length} documents available</p>
      </div>
      {Object.entries(grouped).map(([type, items]) => (
        <div key={type}>
          <h2 className="text-xs font-bold text-[#2a6496] uppercase tracking-wider mb-2 capitalize">{type.replace('_', ' ')}</h2>
          <div className="space-y-2">
            {items.map(doc => (
              <Card key={doc.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#f5f9fb] flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-[#4a9ac5]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1e3a50]">{doc.title}</p>
                    <p className="text-[10px] font-medium text-[#8faab8]">{new Date(doc.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={`text-[9px] font-bold capitalize ${TYPE_COLORS[doc.type] || TYPE_COLORS.document}`}>{doc.type?.replace('_', ' ')}</Badge>
                  {doc.url && <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-[#4a9ac5] hover:text-[#2a6496]"><ExternalLink className="w-4 h-4" /></a>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
      {docs.length === 0 && (
        <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center">
          <FileText className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-sm font-medium text-[#6b8a9e]">Documents will appear here as they're shared by the HDH team.</p>
        </CardContent></Card>
      )}
    </div>
  );
}
